import { useState } from 'react';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { fromDateRange, toDayjsRange } from '@/lib/date';

import { WARRANTY_STATUSES, WARRANTY_STATUS_META } from '../constants';
import type { WarrantyClaimFilters, WarrantyClaimStatus } from '../types';

import styles from './warranty-claims-filter-drawer.module.scss';

export type WarrantyClaimsFilterDrawerProps = {
  open: boolean;
  /** The currently-applied filters; seeds the draft each time the drawer opens. */
  value: WarrantyClaimFilters;
  onClose: () => void;
  onApply: (next: WarrantyClaimFilters) => void;
};

/**
 * Filter panel (Figma 2715-417816): multi-select Warranty Status pills + a Submission Date
 * range. Edits a local draft so Cancel discards and the previously-applied state is retained
 * (story 101872). Apply commits the draft.
 */
export function WarrantyClaimsFilterDrawer({
  open,
  value,
  onClose,
  onApply,
}: WarrantyClaimsFilterDrawerProps) {
  const [draft, setDraft] = useState<WarrantyClaimFilters>(value);

  // Re-seed the draft from the applied value whenever the drawer (re)opens.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(value);
  }

  const toggleStatus = (status: WarrantyClaimStatus) => {
    setDraft((d) => ({
      ...d,
      statuses: d.statuses.includes(status)
        ? d.statuses.filter((s) => s !== status)
        : [...d.statuses, status],
    }));
  };

  const range = toDayjsRange(draft.from && draft.to ? [draft.from, draft.to] : null);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={() => onApply(draft)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <div className={styles.body}>
        <section className={styles.section}>
          <h3 className={styles.label}>Warranty Status</h3>
          <div className={styles.pills}>
            {WARRANTY_STATUSES.map((status) => {
              const selected = draft.statuses.includes(status);
              return (
                <button
                  key={status}
                  type="button"
                  className={`${styles.pill} ${selected ? styles.pillOn : ''}`}
                  aria-pressed={selected}
                  onClick={() => toggleStatus(status)}
                >
                  {WARRANTY_STATUS_META[status].label}
                </button>
              );
            })}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.label}>Submission Date</h3>
          <DateRangePicker
            value={range}
            onChange={(next) =>
              setDraft((d) => {
                const iso = fromDateRange(next ?? null);
                return { ...d, from: iso?.[0] ?? null, to: iso?.[1] ?? null };
              })
            }
            allowClear
          />
        </section>
      </div>
    </Drawer>
  );
}
